import { useEffect, useRef } from 'react';
import {
  getOptions,
  type Answers,
  type AnswerValue,
  type Chapter,
  type Question,
} from '@gbcmc/survey-core';
import { OptionCards } from '../components/OptionCards';
import { LikertScale } from '../components/LikertScale';
import { OpenField } from '../components/OpenField';
import { MatrixQuestion } from '../components/MatrixQuestion';
import { ErrorMessage } from '../components/ErrorMessage';
import { PrimaryButton, SecondaryButton } from '../components/Buttons';

interface QuestionScreenProps {
  question: Question;
  chapter: Chapter;
  qIndex: number;
  qTotal: number;
  answers: Answers;
  setAnswer: (id: string, value: AnswerValue) => void;
  onBack: () => void;
  onNext: () => void;
  canGoBack: boolean;
  error: string | null;
}

// Pantalla de pregunta (handoff §11.3).
export function QuestionScreen({
  question: q,
  chapter,
  qIndex,
  qTotal,
  answers,
  setAnswer,
  onBack,
  onNext,
  canGoBack,
  error,
}: QuestionScreenProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [q.id]);

  const value = answers[q.id];

  let input = null;
  if (q.type === 'single') {
    input = (
      <OptionCards
        options={getOptions(q, answers)}
        value={value as string | undefined}
        compact={q.compact}
        name={q.text}
        onChange={(v) => setAnswer(q.id, v)}
      />
    );
  } else if (q.type === 'likert') {
    input = (
      <LikertScale
        value={value as number | undefined}
        anchors={q.anchors}
        name={q.text}
        onChange={(v) => setAnswer(q.id, v)}
      />
    );
  } else if (q.type === 'open') {
    input = (
      <OpenField
        value={value as string | undefined}
        placeholder={q.placeholder}
        label={q.text}
        onChange={(v) => setAnswer(q.id, v)}
      />
    );
  } else {
    input = (
      <MatrixQuestion
        rows={q.rows}
        columns={q.columns}
        value={value as Record<string, string> | undefined}
        missing={!!error}
        onChange={(v) => setAnswer(q.id, v)}
      />
    );
  }

  return (
    <main className="gb-screen gb-screen--question">
      <section className={'gb-card gb-anim' + (q.type === 'matrix' ? ' gb-card--wide' : '')} key={q.id}>
        <p className="gb-card__eyebrow">
          Capítulo {chapter.num} · {chapter.label}
        </p>
        {q.context ? <p className="gb-card__context">{q.context}</p> : null}
        <h1 className="gb-card__question" tabIndex={-1} ref={headingRef}>
          {q.text}
        </h1>
        {q.help ? <p className="gb-card__help">{q.help}</p> : null}
        {input}
        <ErrorMessage>{error}</ErrorMessage>
        <div className="gb-card__nav">
          {canGoBack ? <SecondaryButton onClick={onBack}>Atrás</SecondaryButton> : <span />}
          <PrimaryButton onClick={onNext}>Continuar</PrimaryButton>
        </div>
        <div className="gb-card__dots" aria-hidden="true">
          {Array.from({ length: qTotal }, (_, i) => (
            <span
              key={i}
              className={'gb-dot' + (i < qIndex ? ' is-done' : i === qIndex ? ' is-now' : '')}
            />
          ))}
        </div>
      </section>
    </main>
  );
}
